import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { TRASH_COLLECTIONS, collectionLabel, trashItemTitle } from './trashCollections'

/**
 * @typedef {object} TrashItem
 * @property {string} Collection - Collectie waar het onderdeel in staat
 * @property {string} DocID - Firestore-document-id
 * @property {string} Title - Wat de gebruiker herkent
 * @property {object} [DeletedAt] - Firestore-timestamp, kan ontbreken
 * @property {string} DeletedBy - Wie het verwijderde, kan leeg zijn
 */

/**
 * @typedef {object} TrashResult
 * @property {Array<TrashItem>} items - Alles wat verwijderd is, nieuwste eerst
 * @property {Array<string>} mislukt - Collecties die niet gelezen konden worden
 * @property {Array<string>} afgekapt - Soorten waarvan de limiet is geraakt
 */

/**
 * Haalt alles op wat in deze omgeving verwijderd is.
 *
 * Elke collectie wordt apart bevraagd op `CompagnyID` + `Deleted`. Firestore kan
 * niet over collecties heen zoeken, dus dat zijn evenveel queries als
 * collecties — ze lopen naast elkaar, en deze pagina wordt zelden geopend.
 * Twee gelijkheidsfilters hebben geen samengestelde index nodig.
 *
 * Er wordt bewust niet gesorteerd in de query: een `orderBy` op `DeletedAt`
 * laat juist de documenten wegvallen die dat veld niet hebben, en dat zijn er
 * honderden — verwijderd door code die alleen `Deleted` zette.
 * AI-CONTEXT: See docs/pitfalls.md#database-firestore-orderby-excludes-missing-field
 *
 * @param {object} [opties]
 * @param {number} [opties.perCollectie] - Hoeveel er per collectie hoogstens opgehaald wordt
 * @returns {Promise<TrashResult>}
 */
const fetchTrashItems = async ({ perCollectie = 300 } = {}) => {
    /** @type {Array<string>} */
    const mislukt = []
    /** @type {Array<string>} */
    const afgekapt = []

    const resultaten = await Promise.all(TRASH_COLLECTIONS.map(async ({ collection }) => {
        try {
            const snap = await db.collection(collection)
                .where('CompagnyID', '==', client)
                .where('Deleted', '==', true)
                .limit(perCollectie)
                .get()

            // Nooit stil afkappen: de pagina moet kunnen zeggen dat er meer is.
            if (snap.size >= perCollectie) afgekapt.push(collectionLabel(collection))

            return snap.docs.map((doc) => {
                const data = doc.data()
                return {
                    ...data,
                    Collection: collection,
                    DocID: doc.id,
                    Title: trashItemTitle(collection, data),
                    DeletedBy: data?.DeletedBy || '',
                }
            })
        } catch (err) {
            // Eén collectie zonder leesrecht mag de rest van de lijst niet
            // meenemen, maar mag ook niet stil verdwijnen.
            console.error(`Prullenbak: ${collection} kon niet gelezen worden`, err)
            mislukt.push(collection)
            return []
        }
    }))

    const items = resultaten.flat()

    // Client-side sorteren, om dezelfde reden als hierboven. Regels zonder
    // verwijdermoment zakken naar onderen in plaats van te verdwijnen.
    items.sort((a, b) => {
        const ta = a.DeletedAt?.toDate ? a.DeletedAt.toDate().getTime() : 0
        const tb = b.DeletedAt?.toDate ? b.DeletedAt.toDate().getTime() : 0
        return tb - ta
    })

    return { items, mislukt, afgekapt }
}

export default fetchTrashItems
